/**
 * Layout Components - プロフェッショナルデザインシステム
 * レスポンシブ・アクセシブル・スケーラブルなレイアウト
 */

import React, { forwardRef } from 'react';
import { tokens } from '../tokens';

// メインコンテナコンポーネント
const Container = forwardRef(({
  children,
  maxWidth = 'xl',
  padding = true,
  center = true,
  className = '',
  ...props
}, ref) => {
  const maxWidths = {
    sm: tokens.breakpoints.sm,
    md: tokens.breakpoints.md,
    lg: tokens.breakpoints.lg,
    xl: tokens.breakpoints.xl,
    '2xl': tokens.breakpoints['2xl'],
    full: '100%',
  };
  
  const containerStyles = {
    width: '100%',
    maxWidth: maxWidths[maxWidth],
    margin: center ? '0 auto' : '0',
    padding: padding ? `0 ${tokens.spacing[4]}` : '0',
    
    // レスポンシブパディング
    [`@media (min-width: ${tokens.breakpoints.sm})`]: {
      padding: padding ? `0 ${tokens.spacing[6]}` : '0',
    },
    
    [`@media (min-width: ${tokens.breakpoints.lg})`]: {
      padding: padding ? `0 ${tokens.spacing[8]}` : '0',
    },
  };
  
  return (
    <div
      ref={ref}
      style={containerStyles}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
});

Container.displayName = 'Container';

// グリッドシステム
const Grid = forwardRef(({
  children,
  columns = 12,
  gap = 4,
  align = 'stretch',
  justify = 'start',
  className = '',
  ...props
}, ref) => {
  const gridStyles = {
    display: 'grid',
    gridTemplateColumns: typeof columns === 'number' 
      ? `repeat(${columns}, 1fr)` 
      : columns,
    gap: tokens.spacing[gap],
    alignItems: align,
    justifyContent: justify,
  };
  
  return (
    <div
      ref={ref}
      style={gridStyles}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
});

Grid.displayName = 'Grid';

// グリッドアイテム
const GridItem = forwardRef(({
  children,
  colSpan = 1,
  rowSpan = 1,
  colStart,
  rowStart,
  className = '',
  ...props
}, ref) => {
  const gridItemStyles = {
    gridColumn: colStart ? `${colStart} / span ${colSpan}` : `span ${colSpan}`,
    gridRow: rowStart ? `${rowStart} / span ${rowSpan}` : `span ${rowSpan}`,
  };
  
  return (
    <div
      ref={ref}
      style={gridItemStyles}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
});

GridItem.displayName = 'GridItem';

// フレックスボックスコンポーネント
const Flex = forwardRef(({
  children,
  direction = 'row',
  align = 'stretch',
  justify = 'start',
  wrap = 'nowrap',
  gap = 0,
  className = '',
  ...props
}, ref) => {
  const flexStyles = {
    display: 'flex',
    flexDirection: direction,
    alignItems: align,
    justifyContent: justify,
    flexWrap: wrap,
    gap: gap ? tokens.spacing[gap] : '0',
  };
  
  return (
    <div
      ref={ref}
      style={flexStyles}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
});

Flex.displayName = 'Flex';

// スタックコンポーネント（縦積み）
const Stack = forwardRef(({
  children,
  spacing = 4,
  align = 'stretch',
  divider,
  className = '',
  ...props
}, ref) => {
  const childrenArray = React.Children.toArray(children);
  
  const stackStyles = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: align,
  };
  
  return (
    <div
      ref={ref}
      style={stackStyles}
      className={className}
      {...props}
    >
      {childrenArray.map((child, index) => (
        <React.Fragment key={index}>
          <div style={{ 
            marginBottom: index < childrenArray.length - 1 ? tokens.spacing[spacing] : '0' 
          }}>
            {child}
          </div>
          {divider && index < childrenArray.length - 1 && (
            <div style={{ margin: `${tokens.spacing[2]} 0` }}>
              {divider}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
});

Stack.displayName = 'Stack';

// 水平スタックコンポーネント
const HStack = forwardRef(({
  children,
  spacing = 4,
  align = 'center',
  justify = 'start',
  wrap = false,
  divider,
  className = '',
  ...props
}, ref) => {
  const childrenArray = React.Children.toArray(children);
  
  const hstackStyles = {
    display: 'flex',
    flexDirection: 'row',
    alignItems: align,
    justifyContent: justify,
    flexWrap: wrap ? 'wrap' : 'nowrap',
  };
  
  return (
    <div
      ref={ref}
      style={hstackStyles}
      className={className}
      {...props}
    >
      {childrenArray.map((child, index) => (
        <React.Fragment key={index}>
          <div style={{ 
            marginRight: index < childrenArray.length - 1 ? tokens.spacing[spacing] : '0' 
          }}>
            {child}
          </div>
          {divider && index < childrenArray.length - 1 && (
            <div style={{ margin: `0 ${tokens.spacing[2]}` }}>
              {divider}
            </div>
          )}
        </React.Fragment>
      ))}
    </div>
  );
});

HStack.displayName = 'HStack';

// センター配置コンポーネント
const Center = forwardRef(({
  children,
  minHeight,
  className = '',
  ...props
}, ref) => {
  const centerStyles = {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: minHeight || 'auto',
  };
  
  return (
    <div
      ref={ref}
      style={centerStyles}
      className={className}
      {...props}
    >
      {children}
    </div>
  );
});

Center.displayName = 'Center';

// スペーサーコンポーネント
const Spacer = forwardRef(({
  size = 4,
  direction = 'vertical',
  className = '',
  ...props
}, ref) => {
  const spacerStyles = {
    width: direction === 'horizontal' ? tokens.spacing[size] : '100%',
    height: direction === 'vertical' ? tokens.spacing[size] : '100%',
    flexShrink: 0,
  };
  
  return (
    <div
      ref={ref}
      style={spacerStyles}
      className={className}
      aria-hidden="true"
      {...props}
    />
  );
});

Spacer.displayName = 'Spacer';

// ディバイダー（区切り線）コンポーネント
const Divider = forwardRef(({
  orientation = 'horizontal',
  variant = 'solid',
  thickness = 1,
  color = 'gray.200',
  className = '',
  ...props
}, ref) => {
  const isVertical = orientation === 'vertical';
  
  const dividerStyles = {
    border: 'none',
    backgroundColor: tokens.colors.gray[200],
    width: isVertical ? `${thickness}px` : '100%',
    height: isVertical ? '100%' : `${thickness}px`,
    margin: '0',
    flexShrink: 0,
    ...(variant === 'dashed' && {
      backgroundImage: `repeating-linear-gradient(
        ${isVertical ? '0deg' : '90deg'},
        transparent,
        transparent 4px,
        ${tokens.colors.gray[200]} 4px,
        ${tokens.colors.gray[200]} 8px
      )`,
      backgroundColor: 'transparent',
    }),
  };
  
  return (
    <hr
      ref={ref}
      style={dividerStyles}
      className={className}
      role="separator"
      aria-orientation={orientation}
      {...props}
    />
  );
});

Divider.displayName = 'Divider';

// レスポンシブ表示制御
const Show = ({ children, breakpoint, direction = 'up' }) => {
  const mediaQuery = direction === 'up' 
    ? `(min-width: ${tokens.breakpoints[breakpoint]})`
    : `(max-width: ${tokens.breakpoints[breakpoint]})`;
  
  const showStyles = {
    display: 'contents',
    [`@media ${mediaQuery}`]: {
      display: 'none',
    },
  };
  
  return (
    <div style={showStyles}>
      {children}
    </div>
  );
};

const Hide = ({ children, breakpoint, direction = 'up' }) => {
  const mediaQuery = direction === 'up' 
    ? `(min-width: ${tokens.breakpoints[breakpoint]})`
    : `(max-width: ${tokens.breakpoints[breakpoint]})`;
  
  const hideStyles = {
    display: 'contents',
    [`@media ${mediaQuery}`]: {
      display: 'none',
    },
  };
  
  return (
    <div style={hideStyles}>
      {children}
    </div>
  );
};

export {
  Container,
  Grid,
  GridItem,
  Flex,
  Stack,
  HStack,
  Center,
  Spacer,
  Divider,
  Show,
  Hide,
};